import { SCondition, RequestQueryBuilder } from '@nestjsx/crud-request';
import { SortDirection } from '@angular/material/sort';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { TorchAPIConfiguration } from '../../../../config/api.configuration';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Burly } from 'kb-burly';
import { ManyResponse } from '../../../../responses/many.response';
import { map } from 'rxjs/operators';
import { ComponentPainByFleetAtaChapter } from '../../models/component-pain-table-fleet-ata-chapter.model';
import { Cacheable } from 'ts-cacheable';

@Injectable({
  providedIn: 'root'
})
export class TorchComponentPainFleetAtaChapterService {

  constructor(protected config: TorchAPIConfiguration, private httpClient: HttpClient) {
  }


  @Cacheable()
  public queryComponentPainTable(painType: string = '1yr',
                                 pageSize: number = 15,
                                 currentPage: number = 0,
                                 sortDirection: 'ASC' | 'DESC' | '' = 'ASC',
                                 sortBy: string = 'rank',
                                 filters: SCondition | null): Observable<ManyResponse<ComponentPainByFleetAtaChapter>> {
    const qb = RequestQueryBuilder.create();

    qb.setPage(currentPage + 1).setLimit(pageSize);

    if (!!sortDirection && sortDirection != null) {
      qb.sortBy({field: sortBy, order: sortDirection});
    }

    if (!!filters) {
      qb.search(filters);
    }

    let finalURL;
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/component/fleet-atachapter/')
      .addSegment(painType)
      .appendRawQueryString(qb.query(false));

    finalURL = endpoint.get;

    return this.httpClient.get<ManyResponse<ComponentPainByFleetAtaChapter>>(finalURL);
  }

  public getPainIndexMonths(painType: string = '1yr'): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/component/fleet-atachapter/')
      .addSegment(painType)
      .addSegment('/painIndexMonth')
      .get;

    return this.httpClient.get<[{ pain_index_month: string }]>(endpoint).pipe(
      map(response => response.map(v => v.pain_index_month))
    );
  }

  public getMaxPainIndexMonth(painType: string = '1yr'): Observable<string> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/component/fleet-atachapter/')
      .addSegment(painType)
      .addSegment('/maxPainIndexMonth')
      .get;

    return this.httpClient.get<{ pain_index_month: string }>(endpoint).pipe(
      map(response => response.pain_index_month)
    );
  }

  public getDistinctNouns(painType: string = '1yr', contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/component/fleet-atachapter/')
      .addSegment(painType)
      .addSegment('/nouns')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ noun: string }]>(endpoint).pipe(
      map(response => response.map(v => v.noun))
    );
  }

  public getDistinctDescriptions(painType: string = '1yr', contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/component/fleet-atachapter/')
      .addSegment(painType)
      .addSegment('/descriptions')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ description: string }]>(endpoint).pipe(
      map(response => response.map(v => v.description))
    );
  }

  public getDistinctAtaDescriptions(painType: string = '1yr', contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/component/fleet-atachapter/')
      .addSegment(painType)
      .addSegment('/ataDescriptions')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ ATA_DESCRIPTION: string }]>(endpoint).pipe(
      map(response => response.map(v => v.ATA_DESCRIPTION))
    );
  }

  public getDistinctAtaGroups(painType: string = '1yr', contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/component/fleet-atachapter/')
      .addSegment(painType)
      .addSegment('/ataGroups')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ ATA_GROUP: string }]>(endpoint).pipe(
      map(response => response.map(v => v.ATA_GROUP))
    );
  }

  public getDistinctFleets(painType: string = '1yr', contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/component/fleet-atachapter/')
      .addSegment(painType)
      .addSegment('/fleets')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ fleet_cd: string }]>(endpoint).pipe(
      map(response => response.map(v => v.fleet_cd))
    );
  }

  public getDistinctAtaChapters(painType: string = '1yr', contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/pain-index/component/fleet-atachapter/')
      .addSegment(painType)
      .addSegment('/ataChapters')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ rmvl_ata_chapter: string }]>(endpoint).pipe(
      map(response => response.map(v => v.rmvl_ata_chapter))
    );
  }

  public exportToExcel(painType: string = '1yr', painData: ComponentPainByFleetAtaChapter[]) {
    return new Observable(subscriber => {
      let endpoint = Burly(this.config.rootUrl)
        .addSegment('/pain-index/component/fleet-atachapter/')
        .addSegment(painType)
        .addSegment('/export')
        .get;

      const httpOptions: { headers; observe; responseType; } = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        }),
        observe: 'response',
        responseType: 'blob'
      };

      const body = {
        painData: painData
      }
      this.httpClient.post(endpoint, body, httpOptions).subscribe((response) => {
        subscriber.next((response as unknown as HttpResponse<any>).body);
        subscriber.complete();
      });
    });
  }
}
